import React from 'react';
import { Form, Radio, Input, Typography, Row, Col } from 'antd';
import type { FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';
import PhoneNumberInput from './PhoneNumberInput';
import { useUserProfile } from '../contextProviders/UserProfileProvider';
import { HOST_ACCOUNT_TYPE_OPTIONS } from '../constants/hostProfileOptions';
import { HostAccountType } from '../interfaces/heavyDutyMachinery/IHostProfile';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

const { Text } = Typography;

export interface IIdentityLegalValues {
  accountType: HostAccountType;
  legalName: string;
  taxId?: string;
  contactPhone: string;
  billingAddress: string;
}

/**
 * Reads a complete fiscal identity off an already-existing profile from the
 * OTHER role this same person has (a HostProfile if this is
 * ServiceProviderOnboardingWizard checking, or vice versa) -- undefined
 * unless every field this step itself requires is present. TaxId is
 * deliberately excluded from that check: it's optional even on a complete
 * UserBillingProfile (HostProfileController always requires it at creation,
 * ServiceProviderProfileController's own self-registration never does --
 * see IdentityLegalStepProps.requireTaxId below).
 */
export const extractCompleteBillingIdentity = (source?: {
  accountType?: string;
  legalName?: string;
  taxId?: string;
  contactPhone?: string;
  billingAddress?: string;
}): IIdentityLegalValues | undefined => {
  if (!source?.accountType || !source.legalName || !source.contactPhone || !source.billingAddress) return undefined;
  return {
    accountType: source.accountType as HostAccountType,
    legalName: source.legalName,
    taxId: source.taxId,
    contactPhone: source.contactPhone,
    billingAddress: source.billingAddress,
  };
};

interface IdentityLegalStepProps {
  /** The caller's own shared Form instance -- this renders Form.Items directly into it (same "one Form instance spans every step" model both onboarding wizards already use), it never owns a <Form> of its own. */
  form: FormInstance;
  submitting?: boolean;
  /** Current accountType value, lifted up by the caller via its own onValuesChange -- NOT read here via Form.useWatch, which both wizards' own history already found unreliable once a step's Form.Item unmounts (see HostOnboardingWizard's own comment on this). */
  accountType: HostAccountType;
  /** HostProfileController always requires TaxId when creating a fresh UserBillingProfile; ServiceProviderProfileController's own self-registration never does. Defaults to true (Host's own stricter rule). */
  requireTaxId?: boolean;
  /**
   * A complete UserBillingProfile already on file for this person, read from
   * their OTHER role's own profile (see extractCompleteBillingIdentity) --
   * when set, this step shows a read-only summary instead of the form
   * below, and none of these fields are ever rendered (so the caller's own
   * fieldsForStep-style validation list for this step should be empty, and
   * whatever values it submits for these fields end up undefined -- exactly
   * right, since the backend's Create endpoint already reuses this same
   * UserBillingProfile row regardless of what's sent, see that entity's own
   * "existing billing wins" doc comment). No edit action here on purpose:
   * there is no PATCH endpoint reachable during onboarding that could apply
   * a change to it yet, and an "editar" link that silently discarded
   * whatever was typed would be worse than not having one at all.
   */
  existingBilling?: IIdentityLegalValues;
}

/**
 * AccountType + LegalName + TaxId + ContactPhone + BillingAddress -- the
 * fiscal identity of the PERSON (see UserBillingProfile's own doc comment),
 * shared verbatim between HostOnboardingWizard and
 * ServiceProviderOnboardingWizard's own onboarding steps rather than each
 * asking for it independently.
 */
const IdentityLegalStep: React.FC<IdentityLegalStepProps> = ({ form, submitting, accountType, requireTaxId = true, existingBilling }) => {
  const { t } = useTranslation();
  const { userProfile } = useUserProfile();
  const accountPhone = userProfile?.phone;
  const isBusiness = accountType === 'Business';

  // Same "starting point, never pisando lo ya escrito" reasoning as
  // HostOnboardingWizard's own pre-extraction effect -- only relevant while
  // this step's own Form.Items are actually mounted (never when
  // existingBilling is already reusing the other role's row below).
  React.useEffect(() => {
    if (!existingBilling && accountPhone && !form.getFieldValue('contactPhone')) {
      form.setFieldsValue({ contactPhone: accountPhone });
    }
  }, [existingBilling, accountPhone, form]);

  if (existingBilling) {
    const taxIdLabel = translate(
      t,
      existingBilling.accountType === 'Business' ? TRANSLATIONS.HOST_FIELD_TAX_ID_BUSINESS : TRANSLATIONS.HOST_FIELD_TAX_ID_INDIVIDUAL
    );
    return (
      <div>
        <Text type="secondary">{translate(t, TRANSLATIONS.IDENTITY_LEGAL_STEP_ALREADY_SET_HINT)}</Text>
        <div style={{ marginTop: 16, padding: 16, borderRadius: 12, background: 'rgba(0,0,0,0.03)' }}>
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12}>
              <Text type="secondary">{translate(t, TRANSLATIONS.HOST_EDIT_ACCOUNT_TYPE_LABEL)}: </Text>
              <Text strong>
                {translate(
                  t,
                  existingBilling.accountType === 'Business' ? TRANSLATIONS.HOST_OPT_ACCOUNT_TYPE_BUSINESS : TRANSLATIONS.HOST_OPT_ACCOUNT_TYPE_INDIVIDUAL
                )}
              </Text>
            </Col>
            <Col xs={24} sm={12}>
              <Text type="secondary">
                {translate(
                  t,
                  existingBilling.accountType === 'Business' ? TRANSLATIONS.HOST_FIELD_LEGAL_NAME_BUSINESS : TRANSLATIONS.HOST_FIELD_LEGAL_NAME_INDIVIDUAL
                )}
                :{' '}
              </Text>
              <Text strong>{existingBilling.legalName}</Text>
            </Col>
            <Col xs={24} sm={12}>
              <Text type="secondary">{taxIdLabel}: </Text>
              <Text strong>{existingBilling.taxId || '—'}</Text>
            </Col>
            <Col xs={24} sm={12}>
              <Text type="secondary">{translate(t, TRANSLATIONS.HOST_FIELD_CONTACT_PHONE)}: </Text>
              <Text strong>{existingBilling.contactPhone}</Text>
            </Col>
            <Col xs={24}>
              <Text type="secondary">{translate(t, TRANSLATIONS.HOST_FIELD_BILLING_ADDRESS)}: </Text>
              <Text strong>{existingBilling.billingAddress}</Text>
            </Col>
          </Row>
        </div>
      </div>
    );
  }

  return (
    <>
      <Form.Item name="accountType" label={translate(t, TRANSLATIONS.HOST_ONBOARDING_ACCOUNT_TYPE_QUESTION)} rules={[{ required: true }]}>
        <Radio.Group options={HOST_ACCOUNT_TYPE_OPTIONS.map((o) => ({ value: o.value, label: translate(t, o.labelKey) }))} optionType="button" />
      </Form.Item>
      <Form.Item
        name="legalName"
        label={translate(t, isBusiness ? TRANSLATIONS.HOST_FIELD_LEGAL_NAME_BUSINESS : TRANSLATIONS.HOST_FIELD_LEGAL_NAME_INDIVIDUAL)}
        rules={[{ required: true }]}
      >
        <Input disabled={submitting} />
      </Form.Item>
      <Form.Item
        name="taxId"
        label={translate(t, isBusiness ? TRANSLATIONS.HOST_FIELD_TAX_ID_BUSINESS : TRANSLATIONS.HOST_FIELD_TAX_ID_INDIVIDUAL)}
        rules={[{ required: requireTaxId }]}
      >
        <Input disabled={submitting} />
      </Form.Item>
      <Form.Item name="contactPhone" label={translate(t, TRANSLATIONS.HOST_FIELD_CONTACT_PHONE)} rules={[{ required: true }]}>
        <PhoneNumberInput disabled={submitting} />
      </Form.Item>
      <Form.Item name="billingAddress" label={translate(t, TRANSLATIONS.HOST_FIELD_BILLING_ADDRESS)} rules={[{ required: true }]}>
        <Input disabled={submitting} />
      </Form.Item>
    </>
  );
};

export default IdentityLegalStep;
